import React, { useState } from 'react';
import PropTypes from 'prop-types';
import CardRow from '../QuotationHeaderCardRow';
import { Grid, Tooltip, Box } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import Replay from '@mui/icons-material/Replay';
import { Autocomplete, TextField, IconButton, Typography } from '@totalsoft/rocket-ui';
import { emptyString, emptyArray, QuotationsStatus } from 'constants/defaultValues';
import { get } from '@totalsoft/change-tracking-react';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { valueOrDefault } from 'utils/functions';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import { quotationsStatusVar } from 'features/insuranceDocument/variables';
import LoadingWrapper from 'components/common/LoadingWrapper';
import { ConventionNameWrapper } from '../CascoStyles';

const QuotationHeaderComponent = ({
  quotation,
  validation,
  onPropertyChange,
  onRetry,
  onAmountChange,
  index,
  disabled,
  allowEditForNegotiation,
  allowRetry,
  titleRef,
  showTitleTooltip
}) => {
  const { t } = useTranslation();
  const status = useReactiveVar(quotationsStatusVar);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const composeId = code => `Quotation_${code}_${index}`;

  return (
    <Grid container>
      <Grid container item alignItems='center'>
        <CardRow
          flexDisplayedValue={true}
          label={t('Offer.Edit.Casco.Header.ConventionName')}
          value={
            <Tooltip title={showTitleTooltip ? quotation.conventionName |> get : ''} placement={'top'}>
              <ConventionNameWrapper ref={titleRef}>{quotation.conventionName |> get}</ConventionNameWrapper>
            </Tooltip>
          }
        />
        <CardRow
          flexDisplayedValue={true}
          label={t('Offer.Edit.Casco.Header.InsuredAmount', {
            currency: quotation.currencyCode |> get |> valueOrDefault(emptyString)
          })}
          value={
            <TextField
              id={composeId('InsuredAmount')}
              fullWidth
              isNumeric
              value={quotation.insuredAmount |> get |> valueOrDefault(emptyString)}
              onChange={onPropertyChange('insuredAmount')}
              disabled={!allowEditForNegotiation('Comparator.EditQuotation.InsuredAmount')}
              error={!isValid(validation.insuredAmount)}
              helperText={getErrors(validation.insuredAmount)}
            />
          }
        />
        <CardRow
          flexDisplayedValue={true}
          label={t('Offer.Edit.Casco.Header.Periodicity')}
          value={
            <TextField
              id={composeId('Periodicity')}
              fullWidth
              isNumeric
              inputProps={{ decimalScale: 0 }}
              value={quotation.periodicity |> get |> valueOrDefault(emptyString)}
              onChange={onPropertyChange('periodicity')}
              disabled={!allowEditForNegotiation('Comparator.EditQuotation.Periodicity')}
              error={!isValid(validation.periodicity)}
              helperText={getErrors(validation.periodicity)}
            />
          }
        />
        <CardRow
          flexDisplayedValue={false}
          label={t('Offer.Edit.Casco.Header.MonthNo')}
          value={
            <TextField
              value={quotation.monthNo |> get |> valueOrDefault(emptyString)}
              disabled
              fullWidth
              isNumeric
              inputProps={{ decimalScale: 0 }}
            />
          }
        />
        <CardRow
          flexDisplayedValue={true}
          label={t('Offer.Edit.Casco.Header.InsuranceAmountWithCurrency', {
            currency: quotation.currencyCode |> get |> valueOrDefault(emptyString)
          })}
          value={
            <>
              {quotation.errorMessage |> get ? (
                <IconButton
                  id={composeId('ErrorMessage')}
                  size='small'
                  tooltip={quotation.errorMessage |> get |> valueOrDefault(emptyString)}
                  onClick={() => setShowErrorMessage(!showErrorMessage)}
                >
                  <ErrorIcon color='error' fontSize='small' />
                </IconButton>
              ) : (
                <>
                  <Grid item xs={12}>
                    <LoadingWrapper
                      loading={
                        status !== QuotationsStatus.EXPIRED &&
                        !((quotation.insuranceAmount |> get) || (quotation.insuranceAmount |> get) === 0) &&
                        !(quotation.sourceQuotationId |> get)
                      }
                    >
                      {(quotation.sourceQuotationId |> get) === null && !(quotation.manualQuotation |> get) ? (
                        <TextField
                          value={quotation.insuranceAmount |> get |> valueOrDefault(emptyString)}
                          disabled
                          fullWidth
                          isNumeric
                          error={!isValid(validation.insuranceAmount)}
                          helperText={getErrors(validation.insuranceAmount)}
                          endAdornment={
                            <IconButton
                              id={composeId('Retry')}
                              color='primary'
                              size='small'
                              variant='text'
                              tooltip={t('Tooltips.Retry')}
                              onClick={onRetry}
                              disabled={disabled('Comparator.RetryCASCOQuotation') || !allowRetry}
                            >
                              <Replay fontSize='small' />
                            </IconButton>
                          }
                        />
                      ) : (
                        <TextField
                          value={quotation.insuranceAmount |> get}
                          onChange={onAmountChange}
                          fullWidth
                          isNumeric
                          error={!isValid(validation.insuranceAmount)}
                          helperText={getErrors(validation.insuranceAmount)}
                          disabled={!allowEditForNegotiation('Comparator.EditQuotation.InsuranceAmount')}
                        />
                      )}
                    </LoadingWrapper>
                  </Grid>
                </>
              )}
            </>
          }
        />
        <CardRow
          label={t('Offer.Edit.Casco.Header.PeriodMonth')}
          value={
            <TextField
              value={quotation.period |> get |> valueOrDefault(emptyString)}
              disabled
              fullWidth
              isNumeric
              inputProps={{ decimalScale: 0 }}
            />
          }
          flexDisplayedValue={false}
        />
        <CardRow
          flexDisplayedValue={true}
          label={t('Offer.Edit.Casco.Header.TotalInsuranceAmountWithCurrency', {
            currency: quotation.currencyCode |> get |> valueOrDefault(emptyString)
          })}
          value={
            quotation.errorMessage |> get ? (
              t('Offer.Edit.Casco.Header.InsuranceAmountNA')
            ) : (
              <Grid item xs={12}>
                <LoadingWrapper
                  loading={
                    status !== QuotationsStatus.EXPIRED &&
                    !((quotation.totalInsuranceAmount |> get) || (quotation.totalInsuranceAmount |> get) === 0) &&
                    !(quotation.sourceQuotationId |> get)
                  }
                >
                  <TextField
                    value={quotation.totalInsuranceAmount |> get |> valueOrDefault(emptyString)}
                    disabled
                    fullWidth
                    isNumeric
                    error={!isValid(validation.totalInsuranceAmount)}
                    helperText={getErrors(validation.totalInsuranceAmount)}
                  />
                </LoadingWrapper>
              </Grid>
            )
          }
        />
        {showErrorMessage && (
          <Grid container>
            <Grid item>
              <Typography variant='caption'>
                <Box fontStyle='italic'>{quotation.errorMessage |> get}</Box>
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
      <Grid item xs={12}>
        <CardRow
          flexDisplayedValue={false}
          label={t('Offer.Edit.Casco.Header.PartialDamageDeductibleAmountWithCurrency', {
            currency:
              quotation.partialDamageDeductibleCurrency |> get
                ? quotation.partialDamageDeductibleCurrency |> get
                : quotation.currencyCode |> get
          })}
          value={
            <Autocomplete
              id={composeId('Partial_Damage_Deductible')}
              fullWidth
              isSearchable
              isNumeric
              simpleValue
              disabled={disabled('Comparator.EditQuotation.PartialDamageDeductibleAmount')}
              labelKey={'value'}
              valueKey={'value'}
              value={quotation.partialDamageDeductibleAmount |> get |> valueOrDefault(emptyString)}
              onChange={onPropertyChange('partialDamageDeductibleAmount')}
              options={quotation.partialDamageDeductibleCosts |> get |> valueOrDefault(emptyArray)}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <CardRow
          flexDisplayedValue={false}
          label={t('Offer.Edit.Casco.Header.TotalDamageDeductiblePercent')}
          value={
            <Autocomplete
              id={composeId('Total_Damage_Deductible')}
              fullWidth
              isSearchable
              isNumeric
              simpleValue={true}
              disabled={disabled('Comparator.EditQuotation.TotalDamageDeductiblePercent')}
              labelKey={'value'}
              valueKey={'value'}
              value={quotation.totalDamageDeductiblePercent |> get |> valueOrDefault(emptyString)}
              onChange={onPropertyChange('totalDamageDeductiblePercent')}
              options={quotation.totalDamageDeductibleCosts |> get |> valueOrDefault(emptyArray)}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <CardRow
          flexDisplayedValue={true}
          label={t('Offer.Edit.Casco.Header.DiscountAmount', {
            currency: quotation.currencyCode |> get |> valueOrDefault(emptyString)
          })}
          value={
            <TextField
              id={composeId('Discount_Amount')}
              fullWidth
              isNumeric
              value={quotation.discountAmount |> get |> valueOrDefault(emptyString)}
              onChange={onPropertyChange('discountAmount')}
              disabled={disabled('Comparator.EditQuotation.DiscountAmount')}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <CardRow
          flexDisplayedValue={true}
          label={t('Offer.Edit.Casco.Header.DiscountPercent')}
          value={
            <TextField
              id={composeId('Discount_Percent')}
              fullWidth
              isNumeric
              value={quotation.discountPercent |> get |> valueOrDefault(emptyString)}
              onChange={onPropertyChange('discountPercent')}
              disabled={disabled('Comparator.EditQuotation.DiscountPercent')}
            />
          }
        />
      </Grid>
      <Grid item xs={12}></Grid>
    </Grid>
  );
};

QuotationHeaderComponent.propTypes = {
  quotation: PropTypes.object,
  validation: PropTypes.object,
  onPropertyChange: PropTypes.func,
  onRetry: PropTypes.func,
  onAmountChange: PropTypes.func,
  index: PropTypes.number,
  disabled: PropTypes.func,
  allowEditForNegotiation: PropTypes.func,
  allowRetry: PropTypes.bool,
  titleRef: PropTypes.func,
  showTitleTooltip: PropTypes.bool
};

export default QuotationHeaderComponent;
